import {
    OfficeRequestDetailsProps,
    IClinician,
    HeldOfficeProps,
    IOfficeWindowSchedule,
    OfficeRequestStatus,
} from "intake_shared/src/types";
import { IOfficeWindow } from "intake_shared/src/types";
import { useToasts } from "react-toast-notifications";
import { CacheContext } from "../../../../contexts/CacheContext";
import * as React from "react";
import { AclContext } from "../../../../contexts/AclContext";
import moment from "moment";
import store from "../../../../store";
import { DateFormats } from "intake_shared/src/types";
import {
    getTimesUsedByClinicianForTheSelectedDay,
    getTimesUsedByOtherClincians,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import { findAvailableTimeSlotsClinicianCanBook } from "../../../../utils/utils";
import { getStringFromScheduleDate } from "../../../../utils/utils";
import { hasConflictingAvailability } from "../../../../utils/utils";
import { hasConflictingAvailabilityWithCliniciansRemoteSchedules } from "../../../../utils/utils";
import { Dialog } from "../../bits/Dialog/Dialog";
import { defaultOffices } from "../../../../store/cache/location";
import { uniq } from "lodash";
import { Select } from "../../bits/Select/Select";
import { Input } from "../../bits/Input/Input";
import { TextArea } from "../../bits/TextArea/TextArea";
import styles from "./HoldWindow.module.scss";

type Option = {
    label: string;
    value: string;
};

interface HoldWindowProps {
    shouldHoldWindow: boolean;
    setShouldHoldWindow: (value: React.SetStateAction<boolean>) => void;
    detailsToHold: OfficeRequestDetailsProps;
    setRefreshKey: (value: React.SetStateAction<number>) => void;
    action: "hold_window" | "hold_with_changes" | undefined;
    clinicianToApproveData: IClinician;
    heldWindows: HeldOfficeProps[];
}

export default function HoldWindow({
    shouldHoldWindow,
    setShouldHoldWindow,
    detailsToHold,
    setRefreshKey,
    action,
    clinicianToApproveData,
    heldWindows,
}: HoldWindowProps) {
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const { addToast } = useToasts();
    const [reasonForHolding, setReasonForHolding] = React.useState("");
    const [draftHoldWithChanges, setDraftHoldWithChanges] = React.useState({
        from: "8:00",
        to: "9:00",
        office: "",
    });

    const [existingSchedules, setExistingSchedules] = React.useState(
        [] as IOfficeWindowSchedule[]
    );
    const [clinicianList, setClinicianList] = React.useState<IClinician[]>([]);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [slotsAvailableForClinician, setSlotsAvailableForClinician] =
        React.useState<string[]>();
    const [startDateToUse, setStartDateToUse] = React.useState(moment());
    const [endDateToUse, setEndDateToUse] = React.useState(moment());

    const fetchCliniciansByLocation = React.useCallback(async (location: string) => {
        setClinicianList((await store.fetchAllCliniciansByLocation(location)) || []);
    }, []);

    const officeOptions = React.useMemo(() => {
        return sortStringsWithNumberEndings(
            uniq(availableOffices[detailsToHold.location])
        ).map((r) => ({
            label: r,
            value: r,
        }));
    }, [availableOffices, detailsToHold.location]);

    React.useEffect(() => {
        fetchCliniciansByLocation(detailsToHold.location);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailsToHold.location]);

    React.useEffect(() => {
        let schedules: IOfficeWindowSchedule[] = [];
        clinicianList.forEach((c) => {
            schedules = schedules.concat(
                c.office_windows
                    .filter((w) => w.location === detailsToHold.location)
                    .map((w) => {
                        return {
                            clinicianId: c.id,
                            clinicianEmail: c.email,
                            id: w.id,
                            assigned_to: `${c.firstname} ${c.lastname}`,
                            start: w.start,
                            ending_date: w.ending_date,
                            end: w.end,
                            office: w.office,
                            location: detailsToHold.location,
                        };
                    })
            );
        });

        setExistingSchedules([...schedules, ...heldWindows]);
    }, [clinicianList, detailsToHold.location, heldWindows]);

    React.useEffect(() => {
        const timeRegex = /(\d{1,2}):(\d{2})\s*([ap]m)?/i;
        const match = draftHoldWithChanges.to.match(timeRegex);

        const startDate = moment(detailsToHold?.start_date)
            .clone()
            .hour(parseInt(draftHoldWithChanges.from.split(":")[0]))
            .minute(parseInt(draftHoldWithChanges.from.split(":")[1]))
            .second(0)
            .milliseconds(0);
        setStartDateToUse(startDate);

        let endDate;
        if (match) {
            const hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            const period = match[3] ? match[3].toLowerCase() : "am";
            const adjustedHours = period === "pm" ? hours + 12 : hours;

            endDate = moment(detailsToHold?.start_date)
                .clone()
                .hour(adjustedHours)
                .minute(minutes)
                .second(0)
                .milliseconds(0);
            setEndDateToUse(endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        draftHoldWithChanges?.to,
        draftHoldWithChanges?.from,
        draftHoldWithChanges.office,
        detailsToHold,
    ]);

    React.useEffect(() => {
        if (clinicianToApproveData) {
            // get all the times that are not available into one array
            const getAllUnAvailableTimes = () => {
                const timesUsedByClinician =
                    getTimesUsedByClinicianForTheSelectedDay(
                        clinicianToApproveData?.office_windows,
                        moment(detailsToHold?.start_date).format(DateFormats.DAY)
                    );
                const timesUsedByOtherClinicians = getTimesUsedByOtherClincians(
                    existingSchedules,
                    detailsToHold?.location,
                    draftHoldWithChanges.office || detailsToHold?.office,
                    moment(detailsToHold?.start_date).format(DateFormats.DAY)
                );
                const allUnAvailableTimes = [
                    ...timesUsedByClinician,
                    ...timesUsedByOtherClinicians,
                ];
                return allUnAvailableTimes;
            };
            // this is the time slots that are occupied for that day
            const occupiedTimeSlots = getAllUnAvailableTimes();

            const slotsAvailable =
                findAvailableTimeSlotsClinicianCanBook(occupiedTimeSlots);
            setSlotsAvailableForClinician(slotsAvailable);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicianToApproveData, draftHoldWithChanges.office]);

    const handleHoldWindow = async () => {
        if (action === "hold_window") {
            // hold office window so its not accessible to anyone else
            const success = await store.handleHoldOfficeSpace(
                OfficeRequestStatus.HELD,
                detailsToHold.id,
                action,
                reasonForHolding,
                detailsToHold.start_date,
                detailsToHold.end_date,
                detailsToHold.office,
                loggedInUserEmail as string,
                detailsToHold.location,
                detailsToHold.requester
            );
            if (success) {
                addToast("Office window held successfully", {
                    appearance: "success",
                });
                setShouldHoldWindow(false);
                setRefreshKey((prevKey) => prevKey + 1);
            }
        }

        const hasConflict = hasConflictingAvailability(
            existingSchedules,
            startDateToUse.toISOString(),
            endDateToUse.toISOString(),
            draftHoldWithChanges.office || detailsToHold.office,
            detailsToHold.location,
            loggedInUserEmail as string
        );

        const request = await store.getClinicianByEmail(detailsToHold.requester);

        const clincianToApproveRemoteWindows = request?.office_windows.filter(
            (window) => window.location === "Remote"
        );
        const requestHasConflictWithClinicianRemoteSchedules =
            await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                clincianToApproveRemoteWindows as IOfficeWindow[],
                startDateToUse.toISOString(),
                endDateToUse.toISOString()
            );

        if (
            (hasConflict || requestHasConflictWithClinicianRemoteSchedules) &&
            action === "hold_with_changes"
        ) {
            addToast(
                [
                    `
          Conflicting schedule with ${
              hasConflict?.clinicianId || "this clincians's remote schedule"
          },`,
                    `${
                        hasConflict?.location ||
                        requestHasConflictWithClinicianRemoteSchedules?.location
                    }, ${
                        hasConflict?.office ||
                        requestHasConflictWithClinicianRemoteSchedules?.office
                    }`,
                    `@ ${moment(
                        getStringFromScheduleDate(
                            hasConflict?.start ||
                                requestHasConflictWithClinicianRemoteSchedules?.start
                        )
                    ).format(DateFormats.DAY_HOUR_FORMAT)} - ${moment(
                        getStringFromScheduleDate(
                            hasConflict?.end ||
                                requestHasConflictWithClinicianRemoteSchedules?.end
                        )
                    ).format(DateFormats.DAY_HOUR_FORMAT)}`,
                ].join(" "),
                { appearance: "error", autoDismiss: false }
            );
            return;
        }

        if (
            (!hasConflict || !requestHasConflictWithClinicianRemoteSchedules) &&
            action === "hold_with_changes"
        ) {
            // hold office window so its not accessible to anyone else
            const success = await store.handleHoldOfficeSpace(
                OfficeRequestStatus.HELD,
                detailsToHold.id,
                action,
                reasonForHolding,
                startDateToUse.toDate(),
                endDateToUse.toDate(),
                draftHoldWithChanges.office || detailsToHold.office,
                loggedInUserEmail as string,
                detailsToHold.location,
                detailsToHold.requester
            );
            if (success) {
                addToast("Office window held successfully", {
                    appearance: "success",
                });
                setShouldHoldWindow(false);
                setRefreshKey((prevKey) => prevKey + 1);
            }
        }
        setReasonForHolding("");
    };

    return (
        <Dialog
            open={shouldHoldWindow}
            onClose={() => setShouldHoldWindow(false)}
            title={`Hold ${detailsToHold?.location} (${
                detailsToHold?.office
            }) ${moment(detailsToHold?.start_date).format(
                DateFormats.DAY
            )} - ${moment(detailsToHold?.start_date).format(
                DateFormats.HOUR_MIN_FORMAT
            )} - ${moment(detailsToHold?.end_date).format(
                DateFormats.HOUR_MIN_FORMAT
            )} for ${detailsToHold.clinician_name}`}
            onSave={handleHoldWindow}
            saveText="Hold Window"
            isSubmitBtnDisabled={reasonForHolding === ""}
        >
            <div className={styles.holdWindow}>
                <p className={styles.note}>
                    Note: Clinicians will not be able to request this space, any
                    request for this space will join the waitlist.
                </p>
                <div className="v2-form">
                    {action === "hold_with_changes" && (
                        <div className="v2-form">
                            <div>
                                {clinicianToApproveData &&
                                    slotsAvailableForClinician &&
                                    slotsAvailableForClinician?.length > 0 && (
                                        <div className={styles.availableTime}>
                                            {" "}
                                            Available time on{" "}
                                            {moment(
                                                detailsToHold?.start_date
                                            ).format(DateFormats.DAY)}{" "}
                                            you can book for this clinician includes
                                            <p className={styles.availableTimeSlots}>
                                                {" "}
                                                {slotsAvailableForClinician?.join(
                                                    ", "
                                                )}
                                            </p>
                                        </div>
                                    )}
                            </div>
                            <Select
                                label="Office"
                                options={officeOptions}
                                value={officeOptions.find(
                                    (option) =>
                                        option.value ===
                                        (draftHoldWithChanges.office ||
                                            detailsToHold.office)
                                )}
                                onChange={(val) => {
                                    setDraftHoldWithChanges({
                                        ...draftHoldWithChanges,
                                        office: (val as Option).value,
                                    });
                                }}
                                isLongListInDialog
                            />
                            <div className="gridTwo">
                                <Input
                                    label="From"
                                    type="time"
                                    min="07:00"
                                    max="22:00"
                                    step={900}
                                    required
                                    value={moment(
                                        draftHoldWithChanges.from,
                                        "hh:mm A"
                                    ).format("HH:mm")}
                                    onChange={(e) => {
                                        setDraftHoldWithChanges({
                                            ...draftHoldWithChanges,
                                            from: e.target.value,
                                        });
                                    }}
                                />
                                <Input
                                    label="To"
                                    type="time"
                                    min="07:00"
                                    max="22:00"
                                    step={900}
                                    required
                                    value={moment(
                                        draftHoldWithChanges.to,
                                        "hh:mm A"
                                    ).format("HH:mm")}
                                    onChange={(e) => {
                                        setDraftHoldWithChanges({
                                            ...draftHoldWithChanges,
                                            to: e.target.value,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <TextArea
                        isRequired
                        className={styles.reasonForHolding}
                        label=" Provide a reason why you want to hold this window"
                        value={reasonForHolding}
                        onChange={(e) => {
                            setReasonForHolding(e.target.value);
                        }}
                    />
                </div>
            </div>
        </Dialog>
    );
}
